import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useSideTittel } from "@/hooks/useSideTittel";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  LogOut,
  LogIn,
  Settings,
  Users,
  MapPin,
  Building2,
  Search,
  ChevronRight,
  BarChart3, Trophy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  useSideTittel(
    "Alle kommunar – søk og nøkkeltal",
    "Søk opp kva som helst av dei 357 norske kommunane og sjå gjeld, driftsresultat, tenester og bemanning. Tala kjem frå KOSTRA og SSB."
  );
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [fylker, setFylker] = useState([]);
  const [kommuner, setKommuner] = useState([]);
  const [selectedFylke, setSelectedFylke] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchFylker = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fylker`);
      setFylker(response.data);
    } catch (error) {
      console.error("Error fetching fylker:", error);
    }
  }, []);

  const fetchKommuner = useCallback(async (fylkeCode = null) => {
    try {
      setLoading(true);
      const url = fylkeCode
        ? `${API}/kommuner?fylke_code=${fylkeCode}`
        : `${API}/kommuner`;
      const response = await axios.get(url);
      setKommuner(response.data);
    } catch (error) {
      console.error("Error fetching kommunar:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFylker();
    fetchKommuner();
  }, [fetchFylker, fetchKommuner]);

  useEffect(() => {
    if (selectedFylke) {
      fetchKommuner(selectedFylke);
    } else {
      fetchKommuner();
    }
  }, [selectedFylke, fetchKommuner]);

  const filteredKommuner = kommuner.filter((kommune) =>
    kommune.kommune_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleKommuneClick = (kommuneCode) => {
    navigate(`/kommune/${kommuneCode}`);
  };

  return (
    <div className="min-h-screen bg-[#F6F8FB]">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E9F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-[#103766]" strokeWidth={1.5} />
              <h1 className="text-xl font-semibold text-[#1F2A36]">
                Kommuneopprøret - ein side frå Rødt Hordaland
              </h1>
            </div>

            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/topplister")}
                data-testid="topplister-btn"
                className="border-[#E52437]/30 text-[#E52437] hover:bg-[#E52437]/5 hover:text-[#E52437]"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Verst i landet
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/sammenlign")}
                data-testid="compare-btn"
                className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Samanlikn
              </Button>

              {user?.role === "admin" && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/admin")}
                    data-testid="admin-panel-btn"
                    className="text-[#4B647F] hover:text-[#103766]"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Admin
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/admin/users")}
                    data-testid="user-management-btn"
                    className="text-[#4B647F] hover:text-[#103766]"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Brukarar
                  </Button>
                </>
              )}

              {user ? (
                <div className="flex items-center gap-3 pl-4 border-l border-[#E6E9F0]">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-[#1F2A36]">{user?.name}</p>
                    <p className="text-xs text-[#8AA4BF]">{user?.email}</p>
                  </div>
                  {user?.picture && (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-8 h-8 rounded-full"
                    />
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={logout}
                    data-testid="logout-btn"
                    className="text-[#4B647F] hover:text-[#D35E4E]"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={login}
                  data-testid="login-btn"
                  className="border-[#E6E9F0] text-[#4B647F] hover:text-[#103766]"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Logg inn (admin)
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Selection Controls */}
        <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8">
          <CardHeader className="border-b border-[#E6E9F0] pb-4">
            <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#103766]" />
              Vel kommune
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2 block">
                  Fylke
                </label>
                <Select
                  value={selectedFylke}
                  onValueChange={setSelectedFylke}
                  data-testid="fylke-select"
                >
                  <SelectTrigger className="w-full border-[#E6E9F0] focus:ring-[#103766]/20">
                    <SelectValue placeholder="Alle fylke" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle fylke</SelectItem>
                    {fylker.map((fylke) => (
                      <SelectItem key={fylke.fylke_code} value={fylke.fylke_code}>
                        {fylke.fylke_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2 block">
                  Søk kommune
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8AA4BF]" />
                  <Input
                    type="text"
                    placeholder="Søk etter kommune..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="kommune-search"
                    className="pl-10 border-[#E6E9F0] focus:ring-[#103766]/20"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Kommune List */}
        <Card className="bg-white border border-[#E6E9F0] rounded-lg">
          <CardHeader className="border-b border-[#E6E9F0] pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-[#1F2A36]">
                Kommunar
              </CardTitle>
              <span className="text-sm text-[#8AA4BF]">
                {filteredKommuner.length} kommunar
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-pulse text-[#4B647F]">Lastar kommunar...</div>
              </div>
            ) : filteredKommuner.length === 0 ? (
              <div className="p-8 text-center text-[#4B647F]">
                Ingen kommunar funne
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="divide-y divide-[#E6E9F0]">
                  {filteredKommuner.map((kommune) => (
                    <button
                      key={kommune.kommune_code}
                      onClick={() => handleKommuneClick(kommune.kommune_code)}
                      data-testid={`kommune-${kommune.kommune_code}`}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#F6F8FB]/50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#103766]/10 flex items-center justify-center">
                          <Building2
                            className="w-5 h-5 text-[#103766]"
                            strokeWidth={1.5}
                          />
                        </div>
                        <div>
                          <p className="font-medium text-[#1F2A36]">
                            {kommune.kommune_name}
                          </p>
                          <p className="text-sm text-[#8AA4BF]">
                            {kommune.fylke_name} • {kommune.kommune_code}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#8AA4BF]" />
                    </button>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard;
